import { useEffect, useRef, useState } from 'react';

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
}

const steps = [
  {
    number: '01',
    icon: 'ri-search-eye-line',
    title: 'Bezplatná analýza',
    subtitle: 'Spoznáme vás a váš potenciál',
    description:
      'Zadarmo zmapujeme vaše podnikanie, konkurenciu a skryté príležitosti. Na konci hovoru viete presne, kde prichádzate o peniaze a čo s tým.',
    color: 'from-orange-500 to-amber-500',
    bgGlow: 'bg-orange-500/10',
    borderGlow: 'group-hover:border-orange-500/40',
    tag: 'Zdarma · 30 minút',
  },
  {
    number: '02',
    icon: 'ri-roadmap-line',
    title: 'Stratégia na mieru',
    subtitle: 'Žiadny copy-paste. Len váš plán.',
    description:
      'Navrhneme konkrétny akčný plán šitý na vaše ciele a rozpočet. Dostanete jasnú cestu - od nuly po merateľné výsledky - ešte pred podpisom zmluvy.',
    color: 'from-emerald-500 to-teal-500',
    bgGlow: 'bg-emerald-500/10',
    borderGlow: 'group-hover:border-emerald-500/40',
    tag: 'Transparentná cena vopred',
  },
  {
    number: '03',
    icon: 'ri-rocket-2-line',
    title: 'Rýchla realizácia',
    subtitle: 'Spúšťame rýchlo. Bez výhovoriek.',
    description:
      'Okamžite sa pustím do práce. Pravidelné reporty, priamy prístup ku mne a spustenie v dohodnutom termíne - zaručene.',
    color: 'from-violet-500 to-purple-500',
    bgGlow: 'bg-violet-500/10',
    borderGlow: 'group-hover:border-violet-500/40',
    tag: 'Dedline = záväzok',
  },
  {
    number: '04',
    icon: 'ri-line-chart-line',
    title: 'Rast & Optimalizácia',
    subtitle: 'Vaše výsledky rastú každý mesiac.',
    description:
      'Po spustení meráme každý klik, každú konverziu a každé euro. Kontinuálne optimalizujeme, aby ste z investície vyťažili maximum - mesiac po mesiaci.',
    color: 'from-orange-400 to-rose-500',
    bgGlow: 'bg-rose-500/10',
    borderGlow: 'group-hover:border-rose-500/40',
    tag: 'Mesačné reporty & ROI tracking',
  },
];

export default function ProcessSection() {
  const { ref, inView } = useInView();
  const [activeStep, setActiveStep] = useState<number | null>(null);

  return (
    <section id="proces" className="py-28 bg-zinc-950 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-zinc-700/40 to-transparent" />
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-zinc-700/40 to-transparent" />
        <div className="absolute top-1/2 left-1/4 w-72 h-72 rounded-full bg-orange-500/5 blur-3xl" />
        <div className="absolute top-1/3 right-1/4 w-56 h-56 rounded-full bg-amber-500/5 blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: 'radial-gradient(circle, #f97316 1px, transparent 1px)',
            backgroundSize: '48px 48px',
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div
          ref={ref}
          className={`text-center mb-20 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="inline-flex items-center gap-2 bg-zinc-800/60 border border-zinc-700/40 rounded-full px-4 py-1.5 mb-6">
            <i className="ri-flow-chart text-orange-400 text-sm" />
            <span className="text-zinc-300 text-sm font-semibold">Ako to funguje</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-white mb-5 leading-tight">
            Od prvého hovoru{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-400">
              po merateľný rast
            </span>
          </h2>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto leading-relaxed">
            Môj overený 4-krokový proces zabezpečuje, že každý projekt dopadne presne tak,
            ako ste si predstavovali - a ešte lepšie.
          </p>
        </div>

        {/* Desktop timeline */}
        <div className="hidden lg:block">
          {/* Connector line */}
          <div className="relative flex items-start gap-0 mb-0">
            <div
              className={`absolute top-12 left-[12.5%] right-[12.5%] h-px transition-all duration-1000 delay-500 ${inView ? 'opacity-100' : 'opacity-0'}`}
              style={{
                background: 'linear-gradient(to right, #f97316, #fbbf24, #a78bfa, #fb923c)',
              }}
            />
            {/* Animated pulse dots on line */}
            {steps.map((_, i) => (
              <div
                key={i}
                className="absolute top-[41px]"
                style={{ left: `calc(${12.5 + i * 25}% - 6px)` }}
              >
                <div
                  className={`w-3 h-3 rounded-full bg-gradient-to-br ${steps[i].color} transition-all duration-500`}
                  style={{ transitionDelay: `${600 + i * 150}ms` }}
                />
                <div
                  className={`absolute inset-0 rounded-full bg-gradient-to-br ${steps[i].color} animate-ping opacity-40`}
                />
              </div>
            ))}
          </div>

          <div className="grid grid-cols-4 gap-6 pt-20">
            {steps.map((step, i) => (
              <DesktopStepCard
                key={step.number}
                step={step}
                index={i}
                inView={inView}
                isActive={activeStep === i}
                onHover={setActiveStep}
              />
            ))}
          </div>
        </div>

        {/* Mobile/Tablet vertical timeline */}
        <div className="lg:hidden flex flex-col gap-0">
          {steps.map((step, i) => (
            <MobileStepCard key={step.number} step={step} index={i} inView={inView} isLast={i === steps.length - 1} />
          ))}
        </div>

        {/* Bottom CTA */}
        <div
          className={`text-center mt-20 transition-all duration-700 delay-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="inline-flex flex-col sm:flex-row items-center gap-4">
            <button
              onClick={() => { const el = document.querySelector('#kontakt'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 text-white font-bold px-8 py-4 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap text-base"
            >
              Začnite krok 1 zadarmo
              <i className="ri-arrow-right-line" />
            </button>
            <span className="text-zinc-500 text-sm">Žiadne záväzky · Odpoveď do 24 hodín</span>
          </div>
        </div>
      </div>
    </section>
  );
}

interface StepCardProps {
  step: typeof steps[0];
  index: number;
  inView: boolean;
  isActive: boolean;
  onHover: (i: number | null) => void;
}

function DesktopStepCard({ step, index, inView, isActive, onHover }: StepCardProps) {
  return (
    <div
      className={`group relative flex flex-col transition-all duration-500 cursor-default
        ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
      style={{ transitionDelay: `${300 + index * 150}ms` }}
      onMouseEnter={() => onHover(index)}
      onMouseLeave={() => onHover(null)}
    >
      {/* Card */}
      <div
        className={`relative bg-zinc-900 border rounded-2xl p-6 transition-all duration-300 overflow-hidden
          ${isActive ? 'border-zinc-600 -translate-y-2' : 'border-zinc-800/60'}
          ${step.borderGlow}
        `}
      >
        {/* Glow bg */}
        <div
          className={`absolute inset-0 ${step.bgGlow} opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl`}
        />

        <div className="relative z-10">
          {/* Step number */}
          <div className="flex items-center justify-between mb-5">
            <span className={`text-5xl font-black text-transparent bg-clip-text bg-gradient-to-br ${step.color} opacity-20 group-hover:opacity-40 transition-opacity duration-300 leading-none`}>
              {step.number}
            </span>
            <div className={`w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br ${step.color} bg-opacity-10`}>
              <i className={`${step.icon} text-white text-lg`} />
            </div>
          </div>

          {/* Title */}
          <h3 className="text-white font-black text-lg mb-1 leading-tight">{step.title}</h3>
          <p className={`text-transparent bg-clip-text bg-gradient-to-r ${step.color} text-xs font-semibold mb-3`}>
            {step.subtitle}
          </p>

          {/* Description */}
          <p className="text-zinc-400 text-sm leading-relaxed mb-5">{step.description}</p>

          {/* Tag */}
          <div className="inline-flex items-center gap-1.5 bg-zinc-800/80 border border-zinc-700/40 rounded-full px-3 py-1">
            <i className="ri-checkbox-circle-line text-emerald-400 text-xs" />
            <span className="text-zinc-400 text-xs font-medium">{step.tag}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

interface MobileStepCardProps {
  step: typeof steps[0];
  index: number;
  inView: boolean;
  isLast: boolean;
}

function MobileStepCard({ step, index, inView, isLast }: MobileStepCardProps) {
  return (
    <div
      className={`flex gap-5 transition-all duration-500 ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'}`}
      style={{ transitionDelay: `${200 + index * 150}ms` }}
    >
      {/* Left: line + dot */}
      <div className="flex flex-col items-center flex-shrink-0 w-10">
        <div className={`w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br ${step.color} flex-shrink-0`}>
          <i className={`${step.icon} text-white text-lg`} />
        </div>
        {!isLast && (
          <div className="flex-1 w-px bg-gradient-to-b from-zinc-600 to-transparent mt-2 mb-2 min-h-[40px]" />
        )}
      </div>

      {/* Right: content */}
      <div className={`pb-8 ${isLast ? '' : ''}`}>
        <div className="flex items-center gap-2 mb-1">
          <span className={`text-xs font-black text-transparent bg-clip-text bg-gradient-to-r ${step.color}`}>
            KROK {step.number}
          </span>
        </div>
        <h3 className="text-white font-black text-lg mb-1">{step.title}</h3>
        <p className={`text-transparent bg-clip-text bg-gradient-to-r ${step.color} text-xs font-semibold mb-2`}>
          {step.subtitle}
        </p>
        <p className="text-zinc-400 text-sm leading-relaxed mb-3">{step.description}</p>
        <div className="inline-flex items-center gap-1.5 bg-zinc-800/80 border border-zinc-700/40 rounded-full px-3 py-1">
          <i className="ri-checkbox-circle-line text-emerald-400 text-xs" />
          <span className="text-zinc-400 text-xs font-medium">{step.tag}</span>
        </div>
      </div>
    </div>
  );
}
